import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ORDER_STATUS_LABEL, type OrderStatus } from '@artmozaik/shared';
import { api, formatSayi, formatTarih, formatTl, tokens } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import { Kutu, Tablo, type Kolon } from '../ui/Rapor';
import { donemYillariYenile } from '../ui/DonemSecici';
import { gunYaz, paraYaz } from './kasa/ortak';

/**
 * DÖNEMLER — yıl devri: yeni dönemi tek tuşla açma, dönemi kapatma, kapanış / açılış tablosu.
 *
 * Panel dönem başına ayrı veritabanı tutmaz; kayıtlar süreklidir. Bu ekran yalnız (a) kapanış
 * fotoğrafını, (b) yeni dönemin açılış değerlerini, (c) yıla bağlı ayarları ve (d) dönem kilidini
 * yönetir. Hiçbir kayıt silinmez, taşınmaz, kopyalanmaz — ekran bunu açıkça söyler (docs/DONEM.md).
 * Uçlar: `/api/donem*` (apps/api/src/routes/donem.ts). Gayriresmî satırlar API'de süzülür.
 */
type FotoTur = 'KASA_HESAP' | 'EMANET' | 'CEK_PORTFOY' | 'CEK_VERILEN' | 'KREDI' | 'CARI_BAKIYE' | 'ACIK_ALACAK' | 'STOK' | 'SIPARIS_ACIK' | 'OZET';
type Ozet = { alindiAt?: string; itibariyle?: string; gecici?: boolean; kesin?: boolean; yenidenAcma?: { at: string; neden: string }[] };
type DonemKaydi = {
  yil: number; bas: string; bit: string; durum: 'ACIK' | 'KAPALI'; acildiAt: string; acan: string | null; kapandiAt: string | null; kapatan: string | null;
  ozet: Ozet | null; secenek: { hedef?: string; yuzde?: number; numara?: string } | null; not: string | null; yilBitti: boolean; numara: 'YILLI' | 'SUREKLI';
};
type Liste = {
  bugun: string; etkinYil: number; donemler: DonemKaydi[]; sonraki: { yil: number; acilabilir: boolean; neden: string | null };
  bant: { acilacakYil: number | null; kapatilacak: number[] };
  yetki: { yonet: boolean; yenidenAc: boolean; gayriGorur: boolean }; muhasebeAd: string;
};
type Satir = { tur: FotoTur; anahtar: string; ad: string; miktar: number | null; tutarKurus: number | null; resmi: boolean; ek: Record<string, unknown> | null; alindiAt: string };
type Toplam = { tur: FotoTur; para: string; adet: number; tutarKurus: number; miktar: number };
type Foto = {
  yil: number; tip: 'kapanis' | 'acilis'; kaynakYil: number; kaynakVar: boolean; ozet: Ozet | null; durum: 'ACIK' | 'KAPALI' | null; alinmadi: boolean;
  satirlar: Satir[]; toplamlar: Toplam[]; gayriGorur: boolean; muhasebeAd: string;
};
type AcSonuc = {
  yil: number; onceki: number; uygulandi: boolean; erken: boolean; secenek: { hedef: HedefKural; yuzde: number; numara: 'YILLI' | 'SUREKLI' };
  hedef: { cariAdet: number; toplamKurus: number; oncekiAdet: number; oncekiToplamKurus: number; hedefsizKalan: number; zatenYazili: number;
    ornekler: { ad: string; oncekiKurus: number; ciroKurus: number; yeniKurus: number | null }[] };
  numara: { kip: string; teklif: string; siparis: string; fason: string };
  acilis: { itibariyle: string; gecici: boolean; toplamlar: Toplam[] };
};
type Madde = { kod: string; durum: 'TAMAM' | 'UYARI' | 'ENGEL'; baslik: string; aciklama: string; adet: number | null; yol: string | null; yolAd: string | null };
type Denetim = { yil: number; durum: string; kapatilabilir: boolean; maddeler: Madde[] };
type GecFatura = { tur: 'SATIS' | 'ALIS'; id: number; no: string; cari: string; tarih: string; tutarKurus: number; geldiAt: string };
type HedefKural = 'AYNI' | 'ARTIS' | 'GERCEKLESEN' | 'BOS';

const SEKMELER = [
  { id: 'kasa', ad: 'Kasa ve banka', turler: ['KASA_HESAP'] },
  { id: 'emanet', ad: 'Emanet', turler: ['EMANET'] },
  { id: 'cek', ad: 'Çek / Senet', turler: ['CEK_PORTFOY', 'CEK_VERILEN'] },
  { id: 'kredi', ad: 'Krediler', turler: ['KREDI'] },
  { id: 'cari', ad: 'Cariler', turler: ['CARI_BAKIYE', 'ACIK_ALACAK'] },
  { id: 'stok', ad: 'Stok', turler: ['STOK'] },
  { id: 'siparis', ad: 'Siparişler', turler: ['SIPARIS_ACIK'] },
  { id: 'ozet', ad: 'Yıl özeti', turler: ['OZET'] },
] as const;
type SekmeId = (typeof SEKMELER)[number]['id'];
const TUR_AD: Record<FotoTur, string> = {
  KASA_HESAP: 'Kasa ve banka', EMANET: 'Emanet', CEK_PORTFOY: 'Alınan çek / senet', CEK_VERILEN: 'Verilen çek / senet', KREDI: 'Krediler (kalan anapara)',
  CARI_BAKIYE: 'Cari bakiyeleri', ACIK_ALACAK: 'Açık alacaklar', STOK: 'Stok (maliyet değeri)', SIPARIS_ACIK: 'Açık siparişler', OZET: 'Yıl özeti',
};
const HESAP_TUR_AD: Record<string, string> = { KASA: 'Kasa', BANKA: 'Banka', EMANET: 'Emanet hesabı' };
const CEK_DURUM_AD: Record<string, string> = { PORTFOY: 'Portföyde', BANKADA: 'Bankada (tahsilde)', TEMINATTA: 'Teminatta', BEKLIYOR: 'Ödenmedi (bekliyor)' };
const HEDEF_AD: Record<HedefKural, string> = { AYNI: 'Aynı hedef', ARTIS: 'Yüzde artışla', GERCEKLESEN: 'Gerçekleşen ciroya göre', BOS: 'Hedef kurma' };
const ek = <T,>(s: Satir, ad: string): T | undefined => (s.ek?.[ad] as T | undefined);
const para = (s: Satir) => String(s.ek?.para ?? 'TRY');
const tutar = (s: Satir) => (s.tutarKurus == null ? '—' : paraYaz(s.tutarKurus, para(s)));
const Gayri = ({ s }: { s: Satir }) => (s.resmi ? null : <span className="ccd-badge ccd-badge-warning" title="Gayriresmî: yalnız faturasız tarafı görebilen rol görür">gayriresmî</span>);

/** Dosyayı oturum jetonuyla indirir (bağlantıya jeton yazılmaz). */
async function indir(yol: string, ad: string): Promise<void> {
  const cagir = () => fetch(yol, { headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  let r = await cagir();
  if (r.status === 401) { await api('/api/me').catch(() => undefined); r = await cagir(); }
  if (!r.ok) throw new Error('Dosya hazırlanamadı');
  const dosya = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? ad;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = dosya; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function Cekmece({ ust, baslik, kapat, mesgul, children }: { ust: string; baslik: string; kapat: () => void; mesgul?: boolean; children: ReactNode }) {
  return (
    <div className="ccd-drawer-arka" onClick={() => !mesgul && kapat()} role="presentation">
      <aside className="ccd-drawer ccd-kasa-cekmece-genis" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={baslik}>
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div><div className="ccd-eyebrow">{ust}</div><h2 className="ccd-heading">{baslik}</h2></div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} disabled={mesgul}>Kapat</button>
          </div>
          {children}
        </div>
      </aside>
    </div>
  );
}
const Hata = ({ m }: { m: string | null }) => (m
  ? <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{m}</span></div> : null);

/** "Ne olur / ne olmaz" — korkuyu gideren açıklama; hem ekranda hem açma penceresinde. */
function NeOlurNeOlmaz({ yil }: { yil: number }) {
  return (
    <div className="ccd-donemk-ikili">
      <div className="ccd-donemk-olur">
        <b>{yil} dönemi açılınca ne OLUR</b>
        <ul>
          <li>{yil} dönemi "açık" olarak eklenir; {yil - 1} dönemi de açık kalır, çalışmaya devam edersiniz.</li>
          <li>Cari hedefleri {yil} yılına kurulur (seçtiğiniz kurala göre).</li>
          <li>{yil} tarihli yeni teklif, sipariş ve fason numaraları seçtiğiniz biçimde verilir.</li>
          <li>{yil - 1} yılının kapanış rakamları {yil} yılının açılış değerleri olarak gösterilir.</li>
        </ul>
      </div>
      <div className="ccd-donemk-olmaz">
        <b>Ne OLMAZ</b>
        <ul>
          <li>Hiçbir kayıt silinmez, taşınmaz, kopyalanmaz.</li>
          <li>Açık siparişler, çekler, krediler, alacaklar ve stok olduğu gibi durur; yeni yılda kaldığı yerden devam eder.</li>
          <li>Mevcut teklif ve sipariş numaraları değişmez.</li>
          <li>{yil - 1} dönemi kendiliğinden kapanmaz; onu yıl bittikten sonra siz kapatırsınız.</li>
        </ul>
      </div>
    </div>
  );
}

/* ─── Yeni dönemi aç: adım adım ──────────────────────────────────────────── */
function AcSihirbazi({ yil, kapat, acildi }: { yil: number; kapat: () => void; acildi: (mesaj: string) => void }) {
  const [adim, setAdim] = useState(1);
  const [hedef, setHedef] = useState<HedefKural>('AYNI');
  const [yuzde, setYuzde] = useState('0');
  const [numara, setNumara] = useState<'YILLI' | 'SUREKLI'>('YILLI');
  const [onizleme, setOnizleme] = useState<AcSonuc | null>(null);
  const [onay, setOnay] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const yuzdeSayi = Number(yuzde.replace(',', '.'));
  const yuzdeGerekli = hedef === 'ARTIS' || hedef === 'GERCEKLESEN';
  const yuzdeHata = yuzdeGerekli && (!Number.isFinite(yuzdeSayi) || yuzdeSayi < -90 || yuzdeSayi > 1000) ? 'Yüzde −90 ile 1000 arasında bir sayı olmalı' : null;
  const govde = useMemo(() => ({ hedef, yuzde: yuzdeGerekli ? yuzdeSayi : 0, numara }), [hedef, yuzdeGerekli, yuzdeSayi, numara]);

  async function onizle() {
    setMesgul(true); setHata(null);
    try { setOnizleme(await api<AcSonuc>('/api/donem/ac', { method: 'POST', body: JSON.stringify(govde) })); setOnay(false); setAdim(3); }
    catch (e) { setHata((e as Error).message); } finally { setMesgul(false); }
  }
  async function ac() {
    setMesgul(true); setHata(null);
    try {
      const r = await api<AcSonuc>('/api/donem/ac?uygula=1', { method: 'POST', body: JSON.stringify(govde) });
      acildi(`${r.yil} dönemi açıldı. ${r.hedef.cariAdet} carinin hedefi kuruldu; ${r.onceki} dönemi açık kalmaya devam ediyor.`);
    } catch (e) { setHata((e as Error).message); setMesgul(false); }
  }
  const o = onizleme;
  return (
    <Cekmece ust={`Adım ${Math.min(adim, 4)} / 4`} baslik={`${yil} dönemini aç`} kapat={kapat} mesgul={mesgul}>
      <ol className="ccd-donemk-adimlar" aria-label="Adımlar">
        {['Ne olacak', 'Seçenekler', 'Önizleme', 'Onay'].map((a, i) => <li key={a} className={adim === i + 1 ? 'ccd-is-active' : adim > i + 1 ? 'ccd-is-done' : ''}>{i + 1}. {a}</li>)}
      </ol>
      <Hata m={hata} />

      {adim === 1 && (
        <>
          <NeOlurNeOlmaz yil={yil} />
          <div className="ccd-meta">Yıl bitmeden de açabilirsiniz: iki dönem bir süre birlikte açık kalır. Sonraki adımda seçenekleri belirler, uygulamadan önce rakamları görürsünüz. <Ipucu id="donem-acma" /></div>
          <div className="ccd-row" style={{ justifyContent: 'flex-end' }}><button type="button" className="ccd-btn ccd-btn-primary" onClick={() => setAdim(2)}>Devam →</button></div>
        </>
      )}

      {adim === 2 && (
        <>
          <div className="ccd-field">
            <div className="ccd-field-label">Cari hedefleri {yil} yılına nasıl kurulsun? <Ipucu id="donem-hedef" /></div>
            <div className="ccd-donemk-secenek" role="radiogroup" aria-label="Hedef kuralı">
              {([
                ['AYNI', `${yil - 1} hedefinin aynısı`, 'Her carinin hedefi olduğu gibi yeni yıla yazılır.'],
                ['ARTIS', 'Yüzde artışla', `${yil - 1} hedefi × (1 + yüzde). Örnek: 1.000.000 ₺ hedef, %25 → 1.250.000 ₺.`],
                ['GERCEKLESEN', 'Gerçekleşen ciroya göre', `Carinin ${yil - 1} cirosu (KDV hariç, bugüne kadar) × (1 + yüzde). Cirosu olmayan cariye hedef yazılmaz.`],
                ['BOS', 'Hedef kurma', 'Yeni yılın hedeflerini sonra, Tanımlar ve bakiyeler ekranından kendiniz yazarsınız.'],
              ] as const).map(([id, ad, aciklama]) => (
                <label key={id} className={`ccd-donemk-sec${hedef === id ? ' ccd-is-active' : ''}`}>
                  <input type="radio" name="hedef" checked={hedef === id} onChange={() => setHedef(id)} />
                  <span><b>{ad}</b><span className="ccd-meta">{aciklama}</span></span>
                </label>
              ))}
            </div>
            {yuzdeGerekli && (
              <div className="ccd-row" style={{ gap: 8, alignItems: 'center' }}>
                <label className="ccd-field-label" htmlFor="donem-yuzde">Yüzde</label>
                <input id="donem-yuzde" className="ccd-input" style={{ maxWidth: 110 }} inputMode="decimal" value={yuzde} onChange={(e) => setYuzde(e.target.value)} aria-invalid={Boolean(yuzdeHata)} />
                <span className="ccd-meta">% (eksi yazarsanız hedef düşer)</span>
              </div>
            )}
            {yuzdeHata && <div className="ccd-field-error">{yuzdeHata}</div>}
          </div>
          <div className="ccd-field">
            <div className="ccd-field-label">{yil} belge numaraları <Ipucu id="belge-no-bicimi" /></div>
            <div className="ccd-donemk-secenek" role="radiogroup" aria-label="Numara biçimi">
              {([
                ['YILLI', `Yıllı: T-${yil}-0001`, `${yil} tarihli teklif, sipariş ve fason numaraları yılla başlar ve 1'den sayar. Hangi yılın belgesi olduğu numaradan anlaşılır.`],
                ['SUREKLI', 'Sürekli: T-0001 biçiminde devam', 'Hiçbir şey değişmez; numaralar kaldığı yerden sürer.'],
              ] as const).map(([id, ad, aciklama]) => (
                <label key={id} className={`ccd-donemk-sec${numara === id ? ' ccd-is-active' : ''}`}>
                  <input type="radio" name="numara" checked={numara === id} onChange={() => setNumara(id)} />
                  <span><b>{ad}</b><span className="ccd-meta">{aciklama}</span></span>
                </label>
              ))}
            </div>
            <div className="ccd-field-hint">Mevcut numaralar değişmez. Cari kodu, ürün kodu ve destek numarası dönemden bağımsızdır.</div>
          </div>
          <div className="ccd-row-between">
            <button type="button" className="ccd-btn" onClick={() => setAdim(1)} disabled={mesgul}>← Geri</button>
            <button type="button" className="ccd-btn ccd-btn-primary" onClick={() => void onizle()} disabled={mesgul || Boolean(yuzdeHata)}>{mesgul ? 'Hesaplanıyor…' : 'Önizlemeyi göster →'}</button>
          </div>
        </>
      )}

      {adim >= 3 && o && (
        <>
          <div className="ccd-banner"><span className="ccd-banner-icon" aria-hidden="true">i</span>
            <span>Bu bir <b>önizlemedir</b>: henüz hiçbir şey yazılmadı. {o.erken ? `${o.onceki} yılı henüz bitmedi; ${o.yil} açılınca iki dönem birlikte açık olur.` : `${o.onceki} yılı bitti; ${o.yil} açıldıktan sonra ${o.onceki} dönemini kapatabilirsiniz.`}</span></div>
          <div className="ccd-donemk-onizleme">
            <div data-fihrist="donem-hedef">
              <div className="ccd-meta">Cari hedefleri · {HEDEF_AD[o.secenek.hedef]}{o.secenek.yuzde ? ` (%${formatSayi(o.secenek.yuzde)})` : ''}</div>
              <b>{o.hedef.cariAdet} cari · {formatTl(o.hedef.toplamKurus)}</b>
              <div className="ccd-meta">{o.onceki}: {o.hedef.oncekiAdet} hedefli cari · {formatTl(o.hedef.oncekiToplamKurus)}
                {o.hedef.hedefsizKalan > 0 && ` · ${o.hedef.hedefsizKalan} cariye hedef yazılmayacak`}
                {o.hedef.zatenYazili > 0 && ` · ${o.hedef.zatenYazili} carinin ${o.yil} hedefi zaten yazılı (dokunulmaz)`}</div>
            </div>
            <div data-fihrist="belge-no-bicimi">
              <div className="ccd-meta">{o.yil} tarihli ilk numaralar</div>
              <b>{o.numara.kip === 'YILLI' ? `${o.numara.teklif} · ${o.numara.siparis} · ${o.numara.fason}` : 'Sürekli biçim — değişiklik yok'}</b>
              <div className="ccd-meta">{o.onceki} tarihli kayıtlar eski biçimde devam eder.</div>
            </div>
          </div>
          {o.hedef.ornekler.length > 0 && o.secenek.hedef !== 'BOS' && (
            <div className="ccd-table-wrap ccd-rapor-tablo ccd-rapor-tablo-ic">
              <table className="ccd-table">
                <thead><tr><th className="ccd-th">Cari</th><th className="ccd-th ccd-td-num">{o.onceki} hedefi</th><th className="ccd-th ccd-td-num">{o.onceki} cirosu</th><th className="ccd-th ccd-td-num">{o.yil} hedefi</th></tr></thead>
                <tbody>{o.hedef.ornekler.map((h) => (
                  <tr key={h.ad} className="ccd-tr"><td className="ccd-td">{h.ad}</td><td className="ccd-td ccd-td-num ccd-td-nowrap">{formatTl(h.oncekiKurus)}</td>
                    <td className="ccd-td ccd-td-num ccd-td-nowrap">{formatTl(h.ciroKurus)}</td><td className="ccd-td ccd-td-num ccd-td-nowrap"><b>{h.yeniKurus == null ? 'hedef yok' : formatTl(h.yeniKurus)}</b></td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
          <div data-fihrist="donem-acilis-degerleri">
            <div className="ccd-field-label">{o.yil} açılış değerleri <Ipucu id="donem-acilis-degerleri" /></div>
            <div className="ccd-meta">{gunYaz(o.acilis.itibariyle)} itibarıyla{o.acilis.gecici ? ` — geçici: ${o.onceki} bitene kadar değişir, yıl sonunda kendiliğinden kesinleşir` : ''}.</div>
            {o.acilis.toplamlar.length === 0 ? <div className="ccd-meta">Henüz gösterilecek değer yok.</div> : (
              <ul className="ccd-donemk-toplamlar">
                {o.acilis.toplamlar.map((t) => (
                  <li key={`${t.tur}|${t.para}`}><span>{TUR_AD[t.tur]}{t.tur === 'SIPARIS_ACIK' ? ` · ${formatSayi(t.miktar)} sipariş` : ` · ${t.adet} satır`}</span><b>{paraYaz(t.tutarKurus, t.para)}</b></li>
                ))}
              </ul>
            )}
          </div>
          <label className="ccd-donemk-onay">
            <input type="checkbox" checked={onay} onChange={(e) => { setOnay(e.target.checked); setAdim(e.target.checked ? 4 : 3); }} />
            <span>Okudum: {o.yil} dönemi açılacak, kayıtlarım silinmeyecek ve taşınmayacak, {o.onceki} dönemi açık kalacak.</span>
          </label>
          <div className="ccd-row-between">
            <button type="button" className="ccd-btn" onClick={() => { setAdim(2); setOnay(false); }} disabled={mesgul}>← Seçenekleri değiştir</button>
            <button type="button" className="ccd-btn ccd-btn-primary" onClick={() => void ac()} disabled={mesgul || !onay} title={onay ? undefined : 'Önce onay kutusunu işaretleyin'}>{mesgul ? 'Açılıyor…' : `${o.yil} dönemini aç`}</button>
          </div>
        </>
      )}
    </Cekmece>
  );
}

/* ─── Dönemi kapat: denetim listesi ──────────────────────────────────────── */
const MADDE_ROZET = { TAMAM: ['ccd-badge-success', 'Tamam'], UYARI: ['ccd-badge-warning', 'Bakın'], ENGEL: ['ccd-badge-danger', 'Engel'] } as const;
function KapatCekmece({ yil, yonet, kapat, kapandi }: { yil: number; yonet: boolean; kapat: () => void; kapandi: (mesaj: string) => void }) {
  const [d, setD] = useState<Denetim | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [onay, setOnay] = useState(false);
  const [mesgul, setMesgul] = useState(false);
  const yukle = useCallback(() => { setHata(null); api<Denetim>(`/api/donem/${yil}/kapat`, { method: 'POST', body: '{}' }).then(setD).catch((e) => setHata((e as Error).message)); }, [yil]);
  useEffect(yukle, [yukle]);
  async function uygula() {
    setMesgul(true); setHata(null);
    try { await api(`/api/donem/${yil}/kapat?uygula=1`, { method: 'POST', body: '{}' }); kapandi(`${yil} dönemi kapatıldı. Kapanış fotoğrafı kesinleşti; ${yil} tarihine artık kayıt yazılamaz.`); }
    catch (e) { setHata((e as Error).message); setMesgul(false); }
  }
  const uyari = d?.maddeler.filter((m) => m.durum === 'UYARI').length ?? 0;
  return (
    <Cekmece ust="Dönem kapanışı" baslik={`${yil} dönemini kapat`} kapat={kapat} mesgul={mesgul}>
      <div className="ccd-meta">Kapatınca: kapanış fotoğrafı son kez alınır ve kesinleşir; {yil} tarihine yeni kayıt ya da değişiklik yapılamaz <Ipucu id="donem-kilidi" />. Hiçbir kayıt silinmez. Yönetici dönemi sonradan yeniden açabilir.</div>
      <Hata m={hata} />
      {!d ? !hata && <div className="ccd-meta">Denetleniyor…</div> : (
        <>
          <ul className="ccd-donemk-denetim" data-fihrist="donem-denetim-listesi">
            {d.maddeler.map((m) => (
              <li key={m.kod} className={`ccd-donemk-madde ccd-donemk-${m.durum.toLowerCase()}`}>
                <span className={`ccd-badge ${MADDE_ROZET[m.durum][0]}`}>{MADDE_ROZET[m.durum][1]}</span>
                <div style={{ minWidth: 0 }}>
                  <b>{m.baslik}</b>
                  <div className="ccd-meta">{m.aciklama}</div>
                  {m.durum !== 'TAMAM' && m.yol && m.yol !== '/donem' && <Link className="ccd-link" to={m.yol}>{m.yolAd} ekranına git →</Link>}
                </div>
              </li>
            ))}
          </ul>
          <div className="ccd-row" style={{ gap: 8 }}><button type="button" className="ccd-btn ccd-btn-sm" onClick={yukle} disabled={mesgul}>Yeniden denetle</button><Ipucu id="donem-denetim-listesi" /></div>
          {!d.kapatilabilir ? (
            <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span>
              <span>{d.durum === 'KAPALI' ? 'Bu dönem zaten kapalı.' : 'Kırmızı işaretli engel giderilmeden dönem kapatılamaz.'}</span></div>
          ) : yonet ? (
            <>
              <label className="ccd-donemk-onay">
                <input type="checkbox" checked={onay} onChange={(e) => setOnay(e.target.checked)} />
                <span>{uyari > 0 ? `${uyari} uyarıyı gördüm. ` : ''}{yil} dönemini kapatmak istiyorum; {yil} tarihine artık kayıt yazılamayacağını biliyorum.</span>
              </label>
              <div className="ccd-row" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="ccd-btn ccd-btn-primary" disabled={!onay || mesgul} onClick={() => void uygula()}>{mesgul ? 'Kapatılıyor…' : `${yil} dönemini kapat`}</button>
              </div>
            </>
          ) : <div className="ccd-meta">Dönemi kapatma yetkiniz yok; liste bilgi içindir.</div>}
        </>
      )}
    </Cekmece>
  );
}

function YenidenAcCekmece({ yil, kapat, acildi }: { yil: number; kapat: () => void; acildi: (mesaj: string) => void }) {
  const [neden, setNeden] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  async function uygula() {
    setMesgul(true); setHata(null);
    try { await api(`/api/donem/${yil}/yeniden-ac`, { method: 'POST', body: JSON.stringify({ neden }) }); acildi(`${yil} dönemi yeniden açıldı. İşiniz bitince yeniden kapatmayı unutmayın.`); }
    catch (e) { setHata((e as Error).message); setMesgul(false); }
  }
  return (
    <Cekmece ust="Yalnız yönetici" baslik={`${yil} dönemini yeniden aç`} kapat={kapat} mesgul={mesgul}>
      <div className="ccd-meta">Yeniden açınca {yil} tarihine yeniden kayıt yazılabilir. Kapanış fotoğrafı durur ama kesin sayılmaz; dönemi yeniden kapattığınızda fotoğraf baştan alınır. Bu işlem kayıt izine nedeniyle yazılır.</div>
      <Hata m={hata} />
      <div className="ccd-field">
        <label className="ccd-field-label" htmlFor="donem-neden">Neden yeniden açıyorsunuz? <span className="ccd-required">*</span></label>
        <textarea id="donem-neden" className="ccd-input" rows={3} maxLength={300} value={neden} onChange={(e) => setNeden(e.target.value)} placeholder="Örnek: Aralık ayına ait unutulmuş kasa çıkışı girilecek" />
      </div>
      <div className="ccd-row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="ccd-btn ccd-btn-primary" disabled={mesgul || neden.trim().length < 5} title={neden.trim().length < 5 ? 'Nedeni yazın (en az 5 harf)' : undefined} onClick={() => void uygula()}>{mesgul ? 'Açılıyor…' : 'Dönemi yeniden aç'}</button>
      </div>
    </Cekmece>
  );
}

/* ─── Kapanış / açılış tablosu ───────────────────────────────────────────── */
const toplamSatiri = (satirlar: Satir[], sutun: number, tutarSutunu: number) => {
  const m = new Map<string, number>();
  for (const s of satirlar) if (s.tutarKurus != null) m.set(para(s), (m.get(para(s)) ?? 0) + s.tutarKurus);
  const yazi = [...m.entries()].sort(([a], [b]) => (a === 'TRY' ? -1 : b === 'TRY' ? 1 : a.localeCompare(b))).map(([p, k]) => paraYaz(k, p)).join(' + ') || '—';
  return (
    <tr>
      <td className="ccd-td" colSpan={tutarSutunu}>Toplam ({satirlar.length} satır)</td>
      <td className="ccd-td ccd-td-num ccd-td-nowrap">{yazi}</td>
      {sutun - tutarSutunu - 1 > 0 && <td className="ccd-td" colSpan={sutun - tutarSutunu - 1} />}
    </tr>
  );
};

function FotoTablo({ sekme, satirlar, muhasebeAd }: { sekme: SekmeId; satirlar: Satir[]; muhasebeAd: string }) {
  const al = (...t: FotoTur[]) => satirlar.filter((s) => t.includes(s.tur));
  const bos = 'Bu başlıkta kayıt yok.';
  if (sekme === 'kasa') {
    const xs = al('KASA_HESAP');
    const k: Kolon<Satir>[] = [
      { baslik: 'Hesap', h: (s) => <>{s.ad} <Gayri s={s} /></> },
      { baslik: 'Tür', dar: true, h: (s) => HESAP_TUR_AD[String(ek(s, 'hesapTur'))] ?? '—' },
      { baslik: 'Açılış', sayi: true, fihrist: 'acilis-bakiyesi', h: (s) => paraYaz(Number(ek(s, 'acilisKurus') ?? 0), para(s)) },
      { baslik: 'Girişler', sayi: true, h: (s) => paraYaz(Number(ek(s, 'girisKurus') ?? 0), para(s)) },
      { baslik: 'Çıkışlar', sayi: true, h: (s) => paraYaz(Number(ek(s, 'cikisKurus') ?? 0), para(s)) },
      { baslik: 'Bakiye', sayi: true, fihrist: 'kasa-bakiyesi', h: (s) => <b>{tutar(s)}</b> },
    ];
    return <Tablo kolonlar={k} satirlar={xs} bos={bos} ilk={200} alt={toplamSatiri(xs, 6, 5)} />;
  }
  if (sekme === 'emanet') {
    const xs = al('EMANET');
    const k: Kolon<Satir>[] = [
      { baslik: 'Kimin', h: (s) => <>{s.ad} <Gayri s={s} /></> },
      { baslik: 'Durum', dar: true, h: (s) => (ek(s, 'yon') === 'BIZDE' ? 'Onların parası bizde' : 'Bizim alacağımız') },
      { baslik: 'Net tutar', sayi: true, fihrist: 'emanet-bakiyesi', h: (s) => <b>{tutar(s)}</b> },
    ];
    return <Tablo kolonlar={k} satirlar={xs} bos={bos} ilk={200} alt={toplamSatiri(xs, 3, 2)} />;
  }
  if (sekme === 'cek') {
    const k = (fihrist: string): Kolon<Satir>[] => [
      { baslik: 'Çek / senet', h: (s) => <>{s.ad} <Gayri s={s} /></> },
      { baslik: 'Durum', dar: true, h: (s) => CEK_DURUM_AD[String(ek(s, 'durum'))] ?? String(ek(s, 'durum') ?? '—') },
      { baslik: 'Vade', dar: true, h: (s) => <span className={ek(s, 'vadesiGecmis') ? 'ccd-kasa-cikis' : undefined}>{gunYaz(String(ek(s, 'vade') ?? ''))}</span> },
      { baslik: 'Tutar', sayi: true, fihrist, h: (s) => <b>{tutar(s)}</b> },
    ];
    const alinan = al('CEK_PORTFOY'); const verilen = al('CEK_VERILEN');
    return (
      <div className="ccd-stack">
        <Kutu baslik="Alınan (elimizde ve bankada bekleyen)" duz><Tablo kolonlar={k('cek-portfoy')} satirlar={alinan} bos={bos} ilk={200} alt={toplamSatiri(alinan, 4, 3)} /></Kutu>
        <Kutu baslik="Verilen (henüz ödenmemiş)" duz><Tablo kolonlar={k('cek-verilen')} satirlar={verilen} bos={bos} ilk={200} alt={toplamSatiri(verilen, 4, 3)} /></Kutu>
      </div>
    );
  }
  if (sekme === 'kredi') {
    const xs = al('KREDI');
    const k: Kolon<Satir>[] = [
      { baslik: 'Kredi', h: (s) => <>{s.ad} <Gayri s={s} /></> },
      { baslik: 'Kullanım', dar: true, h: (s) => gunYaz(String(ek(s, 'kullanim') ?? '')) },
      { baslik: 'Kalan taksit', sayi: true, h: (s) => formatSayi(s.miktar ?? 0) },
      { baslik: 'Kalan anapara', sayi: true, fihrist: 'kredi-kalan-anapara', h: (s) => <b>{tutar(s)}</b> },
      { baslik: 'Faiz ve vergiyle', sayi: true, h: (s) => paraYaz(Number(ek(s, 'kalanToplamKurus') ?? 0), para(s)) },
    ];
    return <Tablo kolonlar={k} satirlar={xs} bos={bos} ilk={200} alt={toplamSatiri(xs, 5, 3)} />;
  }
  if (sekme === 'cari') {
    const bakiye = al('CARI_BAKIYE'); const alacak = al('ACIK_ALACAK');
    const kb: Kolon<Satir>[] = [
      { baslik: 'Cari', h: (s) => s.ad },
      { baslik: 'Tür', dar: true, h: (s) => (ek(s, 'cariTur') === 'TEDARIKCI' ? 'Tedarikçi' : 'Müşteri') },
      { baslik: 'Bakiye', sayi: true, fihrist: 'cari-bakiye', ipucu: 'Artı: bize borçlu. Eksi: biz borçluyuz.', h: (s) => <b>{tutar(s)}</b> },
    ];
    const ka: Kolon<Satir>[] = [
      { baslik: 'Cari', h: (s) => <>{s.ad} <Gayri s={s} /></> },
      { baslik: 'Kaynak', dar: true, h: (s) => (ek(s, 'kaynak') === 'FATURASIZ' ? 'Faturasız alacak' : 'Satış faturası') },
      { baslik: 'Adet', sayi: true, h: (s) => formatSayi(s.miktar ?? 0) },
      { baslik: 'Tahsil edilmemiş', sayi: true, fihrist: 'tahsil-edilmemis', h: (s) => <b>{tutar(s)}</b> },
      { baslik: 'Vadesi geçen', sayi: true, fihrist: 'vadesi-gecen', h: (s) => (ek(s, 'kaynak') === 'FATURASIZ' ? '—' : formatTl(Number(ek(s, 'vadesiGecenKurus') ?? 0))) },
    ];
    return (
      <div className="ccd-stack">
        <Kutu baslik="Açık alacaklar" not="Fotoğrafın alındığı andaki tahsil edilmemiş tutarlar." duz><Tablo kolonlar={ka} satirlar={alacak} bos={bos} ilk={200} alt={toplamSatiri(alacak, 5, 3)} /></Kutu>
        <Kutu baslik={`Cari bakiyeleri (${muhasebeAd})`} not={`${muhasebeAd}'ün fotoğraf alındığı andaki rakamıdır; geçmiş bir güne hesaplanamaz.`} duz>
          <Tablo kolonlar={kb} satirlar={bakiye} bos={bos} ilk={200} alt={toplamSatiri(bakiye, 3, 2)} /></Kutu>
      </div>
    );
  }
  if (sekme === 'stok') {
    const xs = al('STOK');
    const k: Kolon<Satir>[] = [
      { baslik: 'Kod', dar: true, h: (s) => <span className="ccd-mono">{String(ek(s, 'code') ?? '')}</span> },
      { baslik: 'Ürün', h: (s) => s.ad },
      { baslik: 'Hane', dar: true, h: (s) => (s.resmi ? 'Resmî' : <Gayri s={s} />) },
      { baslik: 'Miktar', sayi: true, fihrist: 'toplam-stok', h: (s) => `${formatSayi(s.miktar ?? 0, 3)} ${String(ek(s, 'birim') ?? '')}` },
      { baslik: 'Birim maliyet', sayi: true, fihrist: 'birim-maliyet', h: (s) => (ek(s, 'birimMaliyetKurus') == null ? <span className="ccd-muted">bilinmiyor</span> : formatTl(Number(ek(s, 'birimMaliyetKurus')))) },
      { baslik: 'Maliyet değeri', sayi: true, fihrist: 'depo-stok-degeri', h: (s) => <b>{tutar(s)}</b> },
    ];
    return <Tablo kolonlar={k} satirlar={xs} bos={bos} ilk={100} alt={toplamSatiri(xs, 6, 5)} />;
  }
  if (sekme === 'siparis') {
    const xs = al('SIPARIS_ACIK');
    const k: Kolon<Satir>[] = [
      { baslik: 'Aşama', h: (s) => ORDER_STATUS_LABEL[s.anahtar as OrderStatus] ?? s.ad },
      { baslik: 'Sipariş', sayi: true, fihrist: 'acik-siparis', h: (s) => formatSayi(s.miktar ?? 0) },
      { baslik: 'Tutar (KDV dahil)', sayi: true, fihrist: 'siparis-tutari', h: (s) => <b>{tutar(s)}</b> },
      { baslik: 'Tutarı olmayan', sayi: true, h: (s) => formatSayi(Number(ek(s, 'tutarsiz') ?? 0)) },
    ];
    return <Tablo kolonlar={k} satirlar={xs} bos={bos} alt={toplamSatiri(xs, 4, 2)} />;
  }
  const xs = al('OZET').sort((a, b) => Number(ek(a, 'sira') ?? 0) - Number(ek(b, 'sira') ?? 0));
  const k: Kolon<Satir>[] = [
    { baslik: 'Kalem', fihrist: 'donem-kapanis-fotografi', h: (s) => <>{s.ad} <Gayri s={s} /></> },
    { baslik: 'Adet', sayi: true, h: (s) => (s.miktar == null ? '—' : formatSayi(s.miktar)) },
    { baslik: 'Tutar', sayi: true, h: (s) => <b>{tutar(s)}</b> },
  ];
  return <Tablo kolonlar={k} satirlar={xs} bos={bos} />;
}

/* ─── Sayfa ──────────────────────────────────────────────────────────────── */
export default function Donem() {
  const { can } = useAuth();
  const [liste, setListe] = useState<Liste | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [secili, setSecili] = useState<number | null>(null);
  const [tip, setTip] = useState<'kapanis' | 'acilis'>('kapanis');
  const [sekme, setSekme] = useState<SekmeId>('kasa');
  const [foto, setFoto] = useState<Foto | null>(null);
  const [gec, setGec] = useState<GecFatura[]>([]);
  const [pencere, setPencere] = useState<{ tur: 'ac' | 'kapat' | 'yeniden'; yil: number } | null>(null);
  const [mesgul, setMesgul] = useState(false);

  const yukle = useCallback(() => api<Liste>('/api/donem').then((r) => { setListe(r); setHata(null); setSecili((s) => s ?? (r.donemler.find((d) => d.yil === r.etkinYil)?.yil ?? r.donemler[0]?.yil ?? null)); })
    .catch((e) => setHata((e as Error).message)), []);
  useEffect(() => { void yukle(); }, [yukle]);
  const fotoYukle = useCallback(() => {
    if (secili == null) return;
    api<Foto>(`/api/donem/${secili}/fotograf?tip=${tip}`).then(setFoto).catch((e) => setHata((e as Error).message));
    api<{ faturalar: GecFatura[] }>(`/api/donem/${secili}/gec-faturalar`).then((r) => setGec(r.faturalar)).catch(() => setGec([]));
  }, [secili, tip]);
  useEffect(fotoYukle, [fotoYukle]);

  const oldu = (mesaj: string) => { setPencere(null); setBilgi(mesaj); donemYillariYenile(); void yukle().then(fotoYukle); };
  const d = liste?.donemler.find((x) => x.yil === secili) ?? null;
  const yonet = Boolean(liste?.yetki.yonet) && can('period.manage');
  async function tazele() {
    if (!d) return;
    setMesgul(true); setHata(null);
    try { await api(`/api/donem/${d.yil}/fotograf`, { method: 'POST', body: '{}' }); setBilgi(`${d.yil} fotoğrafı yeniden alındı.`); await yukle(); fotoYukle(); }
    catch (e) { setHata((e as Error).message); } finally { setMesgul(false); }
  }
  const sayilar = useMemo(() => {
    const m = new Map<SekmeId, number>();
    for (const s of SEKMELER) m.set(s.id, (foto?.satirlar ?? []).filter((x) => (s.turler as readonly string[]).includes(x.tur)).length);
    return m;
  }, [foto]);

  if (hata && !liste) return <Hata m={hata} />;
  if (!liste) return <div className="ccd-inline-note">Yükleniyor…</div>;
  const s = liste.sonraki;

  return (
    <>
      <div className="ccd-row-between ccd-rapor-baslik" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="ccd-title">Dönemler <Ipucu id="donem-yil" /></h1>
          <div className="ccd-meta">Yıl devri: yeni dönemi açma, biten dönemi kapatma, kapanış ve açılış rakamları.</div>
        </div>
        <div className="ccd-row ccd-rapor-dugmeler" style={{ gap: 8 }}>
          <button className="ccd-btn ccd-btn-sm" onClick={() => window.print()} title="Tarayıcının yazdırma penceresi açılır; hedef olarak PDF seçebilirsiniz">Yazdır / PDF</button>
        </div>
      </div>

      <div className="ccd-banner ccd-donemk-bilgi">
        <span className="ccd-banner-icon" aria-hidden="true">i</span>
        <span><b>Kayıtlarınız dönemle birlikte taşınmaz, silinmez.</b> Panelde her şey tek defterde sürer: açık sipariş, çek, kredi ve alacak yıl değişince kaldığı yerden devam eder.
          Dönem yalnız üç şey yapar: yıl sonu rakamlarının <b>fotoğrafını</b> saklar, yeni yılın hedef ve numara ayarlarını kurar, kapatılan yılın tarihine yazmayı <b>kilitler</b>.</span>
      </div>

      {bilgi && <div className="ccd-banner" role="status"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
      <Hata m={hata} />

      {/* Sıradaki dönem yalnız açılabilir olduğunda (en erken bir önceki yıl içinde) gösterilir. */}
      {yonet && s.acilabilir && (
        <div className="ccd-donemk-ac">
          <div style={{ minWidth: 0 }}>
            <b>{s.yil} dönemi henüz açılmadı</b>
            <div className="ccd-meta">Vakti gelince tek tuşla açın; {s.yil - 1} bitmeden de açabilirsiniz. Uygulamadan önce ne olacağını rakamlarıyla görürsünüz.</div>
          </div>
          <button type="button" className="ccd-btn ccd-btn-primary ccd-donemk-buyuk" onClick={() => setPencere({ tur: 'ac', yil: s.yil })}>{s.yil} dönemini aç</button>
        </div>
      )}

      <div className="ccd-donemk-kartlar">
        {liste.donemler.map((x) => {
          const hazir = x.durum === 'ACIK' && x.yilBitti;
          return (
            <div key={x.yil} className={`ccd-card ccd-donemk-kart${x.yil === secili ? ' ccd-is-active' : ''}${x.durum === 'KAPALI' ? ' ccd-donemk-kapali' : ''}`}>
              <button type="button" className="ccd-donemk-kart-sec" onClick={() => { setSecili(x.yil); setTip('kapanis'); }} aria-pressed={x.yil === secili} title={`${x.yil} rakamlarını göster`}>
                <span className="ccd-donemk-yil">{x.yil}</span>
                <span className={`ccd-badge ${x.durum === 'KAPALI' ? 'ccd-badge-square' : hazir ? 'ccd-badge-warning' : 'ccd-badge-success'}`}>
                  {x.durum === 'KAPALI' ? 'Kapalı' : hazir ? 'Açık · kapanışa hazır' : x.yil === liste.etkinYil ? 'Açık · içinde bulunulan yıl' : 'Açık'}</span>
              </button>
              <dl className="ccd-donemk-bilgiler">
                <div><dt>Açılış</dt><dd>{formatTarih(x.acildiAt)}{x.acan ? ` · ${x.acan}` : ''}</dd></div>
                <div><dt>Kapanış</dt><dd>{x.kapandiAt ? `${formatTarih(x.kapandiAt)}${x.kapatan ? ` · ${x.kapatan}` : ''}` : hazir ? 'Yıl bitti, kapatılmayı bekliyor' : `31 Aralık ${x.yil}'den sonra`}</dd></div>
                <div><dt>Belge numarası</dt><dd>{x.numara === 'YILLI' ? `Yıllı (T-${x.yil}-0001)` : 'Sürekli (T-0001…)'}</dd></div>
                <div><dt>Fotoğraf</dt><dd>{x.ozet?.itibariyle ? `${gunYaz(x.ozet.itibariyle)} itibarıyla · ${x.durum === 'KAPALI' ? 'kesin' : x.ozet.gecici ? 'geçici' : 'kapanışa hazır'}` : 'henüz alınmadı'}</dd></div>
              </dl>
              <div className="ccd-row ccd-donemk-islem" style={{ gap: 8, flexWrap: 'wrap' }}>
                {x.durum === 'ACIK' && yonet && (
                  <button type="button" className={`ccd-btn ccd-btn-sm${hazir ? ' ccd-btn-primary' : ''}`} onClick={() => setPencere({ tur: 'kapat', yil: x.yil })}
                          title={x.yilBitti ? 'Denetim listesini gösterir; onaylamadan kapatılmaz' : 'Yıl bitmeden kapatılamaz; denetim listesine bakabilirsiniz'}>{x.yil}'{sonEk(x.yil)} kapat…</button>
                )}
                {x.durum === 'KAPALI' && (
                  <button type="button" className="ccd-btn ccd-btn-sm" disabled={!liste.yetki.yenidenAc} title={liste.yetki.yenidenAc ? 'Neden yazmanız gerekir' : 'Kapalı dönemi yalnız yönetici yeniden açabilir'}
                          onClick={() => setPencere({ tur: 'yeniden', yil: x.yil })}>Yeniden aç…</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {d && (
        <section className="ccd-stack">
          <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h2 className="ccd-heading">{d.yil} · {tip === 'kapanis' ? 'kapanış fotoğrafı' : 'açılış değerleri'} <Ipucu id={tip === 'kapanis' ? 'donem-kapanis-fotografi' : 'donem-acilis-degerleri'} /></h2>
              <div className="ccd-meta">
                {tip === 'acilis'
                  ? (foto?.kaynakVar ? `${d.yil} açılışı = ${d.yil - 1} kapanışı; ayrı kopya tutulmaz.` : `${d.yil - 1} dönemi tanımlı olmadığı için açılış değeri yok.`)
                  : null}
                {foto?.ozet?.itibariyle && <> {gunYaz(foto.ozet.itibariyle)} itibarıyla · {foto.durum === 'KAPALI' ? 'kesin (dönem kapalı)' : foto.ozet.gecici ? 'geçici: yıl bitene kadar değişir' : 'yıl sonu rakamı; dönem kapatılınca kesinleşir'}
                  {foto.ozet.alindiAt && ` · alındı: ${formatTarih(foto.ozet.alindiAt)}`}</>}
              </div>
            </div>
            <div className="ccd-row ccd-rapor-dugmeler" style={{ gap: 8, flexWrap: 'wrap' }}>
              <div className="ccd-kasa-secim" role="group" aria-label="Hangi değerler">
                <button type="button" aria-pressed={tip === 'kapanis'} onClick={() => setTip('kapanis')}>Kapanış</button>
                <button type="button" aria-pressed={tip === 'acilis'} onClick={() => setTip('acilis')}>Açılış</button>
              </div>
              {yonet && tip === 'kapanis' && d.durum === 'ACIK' && (
                <button type="button" className="ccd-btn ccd-btn-sm" disabled={mesgul} onClick={() => void tazele()} title="Rakamları şimdi yeniden hesaplar ve saklar">{mesgul ? 'Alınıyor…' : foto?.alinmadi ? 'Fotoğrafı al' : 'Fotoğrafı tazele'}</button>
              )}
              <button type="button" className="ccd-btn ccd-btn-sm" disabled={!foto || foto.alinmadi} title="Bütün başlıklar tek dosyada (Excel'de açılır)"
                      onClick={() => void indir(`/api/donem/${d.yil}/fotograf?tip=${tip}&bicim=csv`, `donem-${d.yil}.csv`).catch((e) => setHata((e as Error).message))}>Excel'e aktar</button>
            </div>
          </div>

          {!foto ? <div className="ccd-inline-note">Yükleniyor…</div> : foto.alinmadi ? (
            <div className="ccd-empty">
              <b>{tip === 'kapanis' ? `${d.yil} fotoğrafı henüz alınmadı.` : `${d.yil - 1} fotoğrafı henüz alınmadı.`}</b>
              <div style={{ marginTop: 6 }}>Fotoğraf; yeni dönem açılırken, yıl döndüğünde (1 Ocak) kendiliğinden ve dönem kapatılırken alınır. {yonet && tip === 'kapanis' && d.durum === 'ACIK' ? '"Fotoğrafı al" ile şimdi de alabilirsiniz.' : ''}</div>
            </div>
          ) : (
            <>
              <div className="ccd-tabs ccd-donemk-sekmeler" role="tablist" aria-label="Başlıklar">
                {SEKMELER.map((x) => <button key={x.id} role="tab" aria-selected={sekme === x.id} className="ccd-tab" onClick={() => setSekme(x.id)}>{x.ad}{sayilar.get(x.id) ? ` (${sayilar.get(x.id)})` : ''}</button>)}
              </div>
              {!foto.gayriGorur && <div className="ccd-meta">Yalnız resmî kayıtlar gösterilir.</div>}
              <FotoTablo sekme={sekme} satirlar={foto.satirlar} muhasebeAd={foto.muhasebeAd} />
            </>
          )}

          {d.durum === 'KAPALI' && (
            <Kutu baslik="Kapandıktan sonra gelen faturalar" fihrist="donem-gec-fatura" duz
                  not={`${liste.muhasebeAd} eşitlemesi dönem kilidine takılmaz. Dönem kapandıktan sonra gelen ${d.yil} tarihli faturalar kapanış fotoğrafına dahil değildir.`}>
              {gec.length === 0 ? <div className="ccd-empty">Kapandıktan sonra gelen {d.yil} tarihli fatura yok.</div> : (
                <Tablo<GecFatura> bos="" satirlar={gec} kolonlar={[
                  { baslik: 'Tür', dar: true, h: (f) => (f.tur === 'SATIS' ? 'Satış' : 'Alış') },
                  { baslik: 'Fatura no', dar: true, h: (f) => f.no },
                  { baslik: 'Cari', h: (f) => f.cari },
                  { baslik: 'Fatura tarihi', dar: true, h: (f) => gunYaz(f.tarih) },
                  { baslik: 'Panele geldi', dar: true, h: (f) => formatTarih(f.geldiAt) },
                  { baslik: 'Tutar (KDV dahil)', sayi: true, h: (f) => formatTl(f.tutarKurus) },
                ]} />
              )}
            </Kutu>
          )}
        </section>
      )}

      {pencere?.tur === 'ac' && <AcSihirbazi yil={pencere.yil} kapat={() => setPencere(null)} acildi={oldu} />}
      {pencere?.tur === 'kapat' && <KapatCekmece yil={pencere.yil} yonet={yonet} kapat={() => setPencere(null)} kapandi={oldu} />}
      {pencere?.tur === 'yeniden' && <YenidenAcCekmece yil={pencere.yil} kapat={() => setPencere(null)} acildi={oldu} />}
    </>
  );
}

/** "2026'yı", "2027'yi", "2030'u" — yılın belirtme eki. */
function sonEk(yil: number): string {
  const son = yil % 10; const on = Math.floor((yil % 100) / 10);
  if (son === 0) return on === 0 ? (yil % 1000 === 0 ? 'i' : 'ü') : ['', 'u', 'yi', 'u', 'ı', 'yi', 'ı', 'i', 'i', 'ı'][on];
  return ['', 'i', 'yi', 'ü', 'ü', 'i', 'yı', 'yi', 'i', 'u'][son];
}
